import L, { Map } from "leaflet";
import { Marker, Polygon, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import {
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  Tooltip,
  Button,
  Card,
  CardHeader,
  CardActions,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import { MaterialIcon } from "ui-widgets";
import BoundaryOrPoint from "models/explorer/boundary-or-point";
import { capitalize } from "lodash";
import ResultsList from "./result-list/ResultList";
import { useNavigate } from "react-router-dom";

const defaultMarker = L.ExtraMarkers.icon({
  markerColor: "blue-dark",
  shape: "circle",
  prefix: "i",
});

const ExplorerResults = ({
  pointsAndBoundaries,
  map,
}: {
  pointsAndBoundaries: readonly BoundaryOrPoint[];
  map: Map;
}) => {
  const navigate = useNavigate();

  // console.log("pointsAndBoundaries");
  const [displayList, setDisplayList] = useState<string[]>([]);
  const popupContent = (pointOrBoundary: BoundaryOrPoint) => {
    const displayExtra = (extra: { [x: string]: any }) => {
      try {
        return extra && Object.keys(extra).length > 0 ? (
          <TableContainer sx={{ p: 1 }}>
            <Table>
              <TableBody>
                {Object.keys(extra).map((key) =>
                  key && extra[key] ? (
                    <TableRow key={`extra-${key}-${extra[key]}`}>
                      <TableCell>{key}</TableCell>
                      <TableCell>{extra[key]}</TableCell>
                    </TableRow>
                  ) : (
                    <></>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <></>
        );
      } catch (error) {
        console.warn("Error on extra popup fields");
        return <></>;
      }
    };
    return (
      <Card>
        <Popup className="request-popup" key={"popup-" + pointOrBoundary.guid}>
          <CardHeader
            sx={(theme) => ({
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              borderTopLeftRadius: "9px",
              borderTopRightRadius: "9px",
              m: "-1px",
              overflowWrap: "anywhere",
            })}
            key={`popup-name-${pointOrBoundary.guid}`}
            title={capitalize(pointOrBoundary.name)}
          />
          {pointOrBoundary.description ? (
            <Typography
              key={"popup-description-" + pointOrBoundary.guid}
              sx={{ m: 1 }}
            >
              {pointOrBoundary.description}
            </Typography>
          ) : (
            <></>
          )}
          {displayExtra(pointOrBoundary.extra)}
          <Box sx={{ m: 1 }}>
            {pointOrBoundary.tags && pointOrBoundary.tags.length > 0 ? (
              pointOrBoundary.tags.map((tag) => (
                <Chip
                  key={`tag-${tag}-${pointOrBoundary.guid}`}
                  sx={{ my: 0.5, mr: 1 }}
                  color="primary"
                  label={tag}
                  variant="filled"
                />
              ))
            ) : (
              <></>
            )}
          </Box>
          <CardActions>
            {pointOrBoundary.input_id && pointOrBoundary.input_id.length > 0 ? (
              <Tooltip title="Show inputs">
                <Button
                  variant="contained"
                  startIcon={<MaterialIcon icon="visibility" />}
                  onClick={() =>
                    navigate(
                      `/search?${pointOrBoundary.type === "boundary" ? "boundaries" : "points"}=${pointOrBoundary.name}`,
                    )
                  }
                >
                  View inputs
                </Button>
              </Tooltip>
            ) : (
              <></>
            )}
          </CardActions>
        </Popup>
      </Card>
    );
  };
  const pointResults =
    pointsAndBoundaries.length > 0 ? (
      pointsAndBoundaries.map((pointOrBoundary) => {
        return (
          pointOrBoundary.type === "point" &&
          pointOrBoundary.latitude &&
          pointOrBoundary?.longitude &&
          displayList.includes(pointOrBoundary.guid) &&
          (pointOrBoundary?.styling ? (
            <Marker
              key={"point-" + pointOrBoundary.guid}
              icon={L.ExtraMarkers.icon({
                markerColor:
                  pointOrBoundary?.styling?.markerColor ?? "blue-dark",
                shape: pointOrBoundary?.styling?.shape ?? "circle",
                prefix: pointOrBoundary?.styling?.prefix ?? "i",
                icon: pointOrBoundary?.styling?.icon ?? "",
              })}
              position={[pointOrBoundary.latitude, pointOrBoundary.longitude]}
            >
              {popupContent(pointOrBoundary)}
            </Marker>
          ) : (
            <Marker
              key={"point-" + pointOrBoundary.guid}
              icon={defaultMarker}
              position={[pointOrBoundary.latitude, pointOrBoundary.longitude]}
            >
              {popupContent(pointOrBoundary)}
            </Marker>
          ))
        );
      })
    ) : (
      <></>
    );
  const boundaryResults =
    pointsAndBoundaries.length > 0 ? (
      pointsAndBoundaries.map((pointOrBoundary) => {
        return (
          pointOrBoundary.type === "boundary" &&
          pointOrBoundary.leafletGeojson &&
          displayList.includes(pointOrBoundary.guid) && (
            <Polygon
              key={"boundary-" + pointOrBoundary.guid}
              data-key={"boundary-" + pointOrBoundary.guid}
              pathOptions={{
                color: pointOrBoundary?.styling?.colour ?? "black",
                fillColor: pointOrBoundary?.styling?.colour ?? "black",
              }}
              positions={pointOrBoundary.leafletGeojson}
            >
              {popupContent(pointOrBoundary)}
            </Polygon>
          )
        );
      })
    ) : (
      <></>
    );
  return (
    <>
      <ResultsList
        pointsAndBoundaries={pointsAndBoundaries}
        map={map}
        sendDisplayList={(tableDisplayList) => setDisplayList(tableDisplayList)}
      />
      <MarkerClusterGroup chunkedLoading>{pointResults}</MarkerClusterGroup>
      {boundaryResults}
    </>
  );
};

export default ExplorerResults;
